/**
 * アカウント → 個人設定（縦串11、034、docs/26 §2 → #152、docs/61 §2・★h・★j、Figma「アカウントと氏名（10/8）」③④⑧）
 *   iOS のまとまり（components/GroupList.tsx の設定の行）。PC は真ん中に 560px の列、スマホ版は左右 16px。
 *   上の帯「‹ 前の画面｜保存」（保存は氏名を直したときだけ押せる＝★h）、大きな題「個人設定」、頭に人のアイコン 72・氏名・メール。
 *   1. 氏名：姓・名を行の中で入力（どちらも必須＝★a）。注「全部の会社で同じ氏名になります」（★1：人に1つ。保存すると全部の会社に効く）
 *   2. ログイン：メールアドレス（見るだけ）／パスワード ›（下から出るシート＝★j：いまのパスワードで確かめる・ほかの端末のログインが切れる・メールで知らせ。
 *      パスワードの無い人＝Google・Apple だけは「未設定」でログイン方法へ）／ログイン方法 ›（/account/login-methods。★3）
 *   3. {会社名}：権限（見るだけ）
 *   4. 表示：文字の大きさ・テーマ色（#191、docs/77）・明るさ（#191 の2回目）・写真の引き出し（field の会社だけ。#118）・PC版／スマホ版（#120。出す条件は今のまま）。押した瞬間に保存（人ごと）
 *   5. ログアウト（朱）
 */
import { HOME_MSG,
  ACCOUNT_MENU_MSG,
  ACCOUNT_NAME_MSG,
  DrawerHand,
  OAUTH_MSG,
  PHOTO_ASSIGN_MSG,
  PROFILE_MSG,
  PasswordChangeRequest,
  ProfileUpdate,
  ROLE_INFO,
  SWITCHER_MSG,
  fullName,
  type AccountPreferences,
  type LoginMethods,
  type Me,
  type Profile,
} from "@genbaryoku/contract";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useState } from "react";
import { Link, useNavigate } from "react-router";
import { api, ApiError, messageOf } from "../api.js";
import { ME_KEY, useLogout, useMe } from "../auth.js";
import { BackButton, useBackNav, usePageName } from "../components/BackButton.jsx";
import { GroupDangerRow, GroupInputRow, GroupList, GroupNavRow, GroupSection, GroupValueRow, RowIcon } from "../components/GroupList.jsx";
import { syncPasskeySignals } from "../components/Passkey.jsx";
import { PersonIcon } from "../components/PersonIcon.jsx";
import { Segmented } from "../components/Segmented.jsx";
import { Sheet } from "../components/Sheet.jsx";
import { TextSize } from "../components/TextSize.jsx";
import { ColorModeRow, ThemeColorRow } from "../components/ThemeColor.jsx";
import { ViewSwitchRow } from "../components/ViewSwitch.jsx";
import { ErrorText, Field, HiddenUsername, PasswordInput, PasswordRules, guardComposingEnter, useToast } from "../components/ui.jsx";
import { useModule } from "../core/slots.jsx";

const PROFILE_KEY = ["account-profile"] as const;
/** 設定 → ログイン方法の画面と同じキー（行の右の「Google・パスキー」） */
const METHODS_KEY = ["login-methods"] as const;

/** ログイン方法の行の右（つないだ外部とパスキー。パスワードは「パスワード」の行にあるので書かない） */
function methodsSummary(m: LoginMethods | undefined): string {
  if (!m) return "";
  return [m.google ? "Google" : null, m.apple ? "Apple" : null, m.passkeys.length ? "パスキー" : null].filter(Boolean).join("・");
}

export function AccountProfilePage() {
  const me = useMe();
  const qc = useQueryClient();
  const nav = useNavigate();
  const logout = useLogout();
  const { toast, node } = useToast();
  const drawerOn = useModule("field");
  const formId = useId();
  const sizeId = useId();
  const handId = useId();
  const q = useQuery({ queryKey: PROFILE_KEY, queryFn: () => api<Profile>("GET", "/account/profile") });
  const methods = useQuery({ queryKey: METHODS_KEY, queryFn: () => api<LoginMethods>("GET", "/account/login-methods") });

  // ---- 氏名（★h：直したときだけ「保存」を押せる） ----
  const [family, setFamily] = useState("");
  const [given, setGiven] = useState("");
  const [nameError, setNameError] = useState<string | undefined>();
  useEffect(() => {
    if (!q.data) return;
    setFamily(q.data.familyName);
    setGiven(q.data.givenName);
  }, [q.data]);
  const dirty = !!q.data && (family !== q.data.familyName || given !== q.data.givenName);
  const parsed = ProfileUpdate.safeParse({ familyName: family, givenName: given });
  const saveName = useMutation({
    mutationFn: (v: ProfileUpdate) => api<Profile>("PUT", "/account/profile", v),
    onSuccess: (p) => {
      qc.setQueryData(PROFILE_KEY, p);
      void qc.invalidateQueries({ queryKey: ME_KEY }); // 右上の名前（全部の会社で同じ）
      void syncPasskeySignals(); // 端末のパスキーの候補に出る名前も今の氏名に（#114）
      toast(PROFILE_MSG.saved);
    },
    onError: (e) => {
      const first = e instanceof ApiError && e.problem.errors ? (Object.values(e.problem.errors)[0] as string | undefined) : undefined;
      setNameError(first ?? messageOf(e));
    },
  });
  const submitName = (e: React.FormEvent) => {
    e.preventDefault();
    setNameError(undefined);
    if (!dirty || saveName.isPending) return;
    if (!parsed.success) {
      setNameError(parsed.error.issues[0]?.message);
      return;
    }
    saveName.mutate(parsed.data);
  };

  // ---- パスワードの変更（★j：シート） ----
  const [pwOpen, setPwOpen] = useState(false);
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [pwError, setPwError] = useState<string | undefined>();
  const closePw = () => {
    setPwOpen(false);
    setCur("");
    setNext("");
    setPwError(undefined);
  };
  const changePw = useMutation({
    mutationFn: (v: PasswordChangeRequest) => api<{ ok: true; revokedSessions: number }>("POST", "/account/password", v),
    onSuccess: () => {
      closePw();
      void qc.invalidateQueries({ queryKey: PROFILE_KEY });
      void qc.invalidateQueries({ queryKey: METHODS_KEY });
      toast(PROFILE_MSG.passwordChanged);
    },
    onError: (e) => {
      if (e instanceof ApiError && e.problem.errors) {
        const errs = e.problem.errors as Record<string, string>;
        setPwError(errs.currentPassword ?? errs.newPassword ?? Object.values(errs)[0]);
        document.getElementById(errs.currentPassword ? "pw-cur" : "pw-next")?.focus();
        return;
      }
      setPwError(messageOf(e));
    },
  });
  const submitPw = () => {
    setPwError(undefined);
    const v = PasswordChangeRequest.safeParse({ currentPassword: cur, newPassword: next });
    if (!v.success) {
      setPwError(v.error.issues[0]?.message);
      return;
    }
    if (!changePw.isPending) changePw.mutate(v.data);
  };

  // 戻る（#98、docs/37）：直前の画面があればそこ。無ければ 今日。保存していない氏名は確かめる
  const back = useBackNav({ to: "/home", label: HOME_MSG.menu }, dirty);
  usePageName(ACCOUNT_MENU_MSG.profile);
  if (q.isError) return <ErrorText>{messageOf(q.error)}</ErrorText>;
  if (!q.data || !me.data) return <p className="hint">読み込んでいます…</p>;
  const p = q.data;
  const m = me.data;
  const hasPassword = p.passwordUpdatedAt !== null;
  const savedName = fullName(p);

  return (
    <div className="ppage" data-testid="profile">
      <div className="cotop">
        <BackButton back={back} />
        <span className="cotop-sp" />
        <button type="submit" form={formId} className="btn text cotop-btn is-save" disabled={!dirty || saveName.isPending} data-testid="profile-save">
          {saveName.isPending ? ACCOUNT_MENU_MSG.saving : ACCOUNT_MENU_MSG.save}
        </button>
      </div>
      {back.dialog}
      <h1 className="ptitle">{ACCOUNT_MENU_MSG.profile}</h1>

      <div className="phead" data-testid="profile-head">
        <PersonIcon familyName={p.familyName} givenName={p.givenName} size={72} />
        <b className={"pname" + (savedName ? "" : " is-none")} data-testid="profile-name">
          {savedName || ACCOUNT_NAME_MSG.register}
        </b>
        <span className="pmail">{p.email}</span>
      </div>

      {/* 1. 氏名（人に1つ＝全部の会社で同じ） */}
      <form id={formId} noValidate onSubmit={submitName}>
        <GroupSection title={ACCOUNT_NAME_MSG.section} note={ACCOUNT_NAME_MSG.sameEverywhere} data-testid="profile-name-section">
          <GroupList>
            <GroupInputRow id="profile-family" label={ACCOUNT_NAME_MSG.family}>
              <input
                id="profile-family"
                className="giinp"
                autoComplete="family-name"
                maxLength={50}
                value={family}
                aria-invalid={dirty && family.trim() === "" ? true : undefined}
                onKeyDown={guardComposingEnter}
                onChange={(e) => {
                  setFamily(e.target.value);
                  setNameError(undefined);
                }}
              />
            </GroupInputRow>
            <GroupInputRow id="profile-given" label={ACCOUNT_NAME_MSG.given}>
              <input
                id="profile-given"
                className="giinp"
                autoComplete="given-name"
                maxLength={50}
                value={given}
                aria-invalid={dirty && given.trim() === "" ? true : undefined}
                onKeyDown={guardComposingEnter}
                onChange={(e) => {
                  setGiven(e.target.value);
                  setNameError(undefined);
                }}
              />
            </GroupInputRow>
          </GroupList>
        </GroupSection>
      </form>
      <ErrorText>{nameError ?? (dirty && !parsed.success ? parsed.error.issues[0]?.message : undefined)}</ErrorText>

      {/* 2. ログイン */}
      <GroupSection title={ACCOUNT_MENU_MSG.loginSection}>
        <GroupList>
          <GroupValueRow title={ACCOUNT_MENU_MSG.email} value={p.email} valueTestId="profile-email" />
          <GroupNavRow
            icon={<RowIcon k="key" />}
            title={ACCOUNT_MENU_MSG.password}
            value={hasPassword ? ACCOUNT_MENU_MSG.passwordChange : OAUTH_MSG.passwordUnset}
            onClick={hasPassword ? () => setPwOpen(true) : () => nav("/account/login-methods")}
            buttonTestId="profile-password"
          />
          <GroupNavRow icon={<RowIcon k="shield" />} title={OAUTH_MSG.title} value={methodsSummary(methods.data)} to="/account/login-methods" buttonTestId="profile-login-methods" />
        </GroupList>
      </GroupSection>

      {/* 3. いまの会社：権限（見るだけ） */}
      <GroupSection title={m.tenant.name || SWITCHER_MSG.noName}>
        <GroupList>
          <GroupValueRow title={ACCOUNT_MENU_MSG.role} value={ROLE_INFO[p.role].label} valueTestId="profile-role" />
        </GroupList>
      </GroupSection>

      {/* 4. 表示（押した瞬間に保存＝人ごと。保存ボタンとは別） */}
      <GroupSection title={ACCOUNT_MENU_MSG.displaySection}>
        <GroupList>
          <GroupValueRow icon={<RowIcon k="text" />} title={ACCOUNT_MENU_MSG.textSize} titleId={sizeId} value={<TextSize labelledBy={sizeId} />} />
          {/* テーマ色（#191、docs/77）：押した瞬間に画面全体に当てて人に保存（PC・スマホで同じ） */}
          <ThemeColorRow />
          {/* 明るさ（#191 の2回目、docs/77 §8）：ライト／ダーク／端末に合わせる。押した瞬間に当てて人に保存 */}
          <ColorModeRow />
          {/* 写真の引き出し（スマホで撮った写真を振り分ける）は field の会社だけ（#142 段6 No.49）。設定の値は残す */}
          {drawerOn ? <DrawerHandRow labelId={handId} onSaved={(t) => toast(t)} /> : null}
          <ViewSwitchRow />
        </GroupList>
      </GroupSection>

      {/* 5. ログアウト */}
      <GroupSection>
        <GroupList>
          <GroupDangerRow
            title={ACCOUNT_MENU_MSG.logout}
            disabled={logout.isPending}
            onClick={() => logout.mutate(undefined, { onSettled: () => nav("/login", { replace: true }) })}
            buttonTestId="profile-logout"
          />
        </GroupList>
      </GroupSection>

      <Sheet
        open={pwOpen}
        title={ACCOUNT_MENU_MSG.passwordSheetTitle}
        cancelLabel={ACCOUNT_MENU_MSG.passwordSheetCancel}
        doneLabel={ACCOUNT_MENU_MSG.passwordSheetDone}
        doneDisabled={cur === "" || next === "" || changePw.isPending}
        onCancel={closePw}
        onDone={submitPw}
        testId="password-sheet"
        doneTestId="password-change"
        cancelTestId="password-cancel"
      >
        <div className="pwsheet">
          <HiddenUsername email={p.email} />
          <Field id="pw-cur" label="現在のパスワード">
            <PasswordInput
              id="pw-cur"
              autoComplete="current-password"
              value={cur}
              onChange={(v) => {
                setCur(v);
                setPwError(undefined);
              }}
            />
          </Field>
          <Field id="pw-next" label="新しいパスワード（8文字以上）">
            <PasswordInput
              id="pw-next"
              autoComplete="new-password"
              value={next}
              onChange={(v) => {
                setNext(v);
                setPwError(undefined);
              }}
            />
          </Field>
          <PasswordRules value={next} email={p.email} />
          <ErrorText>{pwError}</ErrorText>
          <p className="gsecnote">{ACCOUNT_MENU_MSG.passwordSheetNote}</p>
          <Link className="btn text" to="/forgot">
            {ACCOUNT_MENU_MSG.forgot}
          </Link>
        </div>
      </Sheet>
      {node}
    </div>
  );
}

/** 写真の引き出しの向き（#118、docs/44 §1 の13）。右から（右手）か左から（左手）。押した瞬間に保存（人ごと＝どの端末でも同じ） */
function DrawerHandRow({ labelId, onSaved }: { labelId: string; onSaved: (m: string) => void }) {
  const qc = useQueryClient();
  const me = useMe();
  const hand = me.data?.preferences?.drawerHand ?? "right";
  const [err, setErr] = useState<string | undefined>();
  const set = async (h: DrawerHand) => {
    if (h === hand || !me.data) return;
    setErr(undefined);
    const prev = me.data;
    qc.setQueryData<Me>(ME_KEY, { ...prev, preferences: { ...prev.preferences, drawerHand: h } });
    try {
      await api<AccountPreferences>("PUT", "/account/preferences", { drawerHand: h });
      onSaved(PHOTO_ASSIGN_MSG.handSaved.replace("{hand}", PHOTO_ASSIGN_MSG.handLabel[h]));
    } catch (e) {
      qc.setQueryData<Me>(ME_KEY, prev);
      setErr(messageOf(e));
    }
  };
  return (
    <GroupValueRow
      icon={<RowIcon k="drawer" />}
      title={err ? <span className="err">{err}</span> : ACCOUNT_MENU_MSG.drawerHand}
      titleId={labelId}
      testId="profile-drawer-hand"
      value={
        <Segmented
          labelledBy={labelId}
          value={hand}
          onChange={(h) => void set(h)}
          options={DrawerHand.options.map((h) => ({ value: h, label: <span title={PHOTO_ASSIGN_MSG.handLabel[h]}>{PHOTO_ASSIGN_MSG.handShort[h]}</span>, testId: `drawer-hand-${h}` }))}
        />
      }
    />
  );
}
